// https://github.com/fizix-io/ra-customizable-datagrid

import React, { useEffect, useState } from "react";

import isEmpty from "lodash/isEmpty";
import filter from "lodash/filter";
import get from "lodash/get";
import { Button, Typography, Box } from "@material-ui/core";

import { Datagrid } from "react-admin";

import { CustomFieldsIcon } from "../iconography";
import SelectionDialog from "./SelectionDialog";
import LocalStorage from "./LocalStorage";

const arrayToSelection = (values) =>
  values.reduce((selection, columnName) => {
    selection[columnName] = true;
    return selection;
  }, {});

// CustomizableDatagrid allows to show/hide columns dynamically
// the preferences are stored in local storage
export const CustomizableDatagrid = ({
  children,
  label = "",
  listSwitch,
  defaultColumns = [],
  storage = LocalStorage,
  storageProp,
  ...props
}) => {
  const getColumnNames = () => {
    return filter(children.map((field) => get(field, ["props", "source"])));
  };

  const getInitialSelection = () => {
    const previousSelection = storage.get(storageProp);

    // if we have a previously stored value, let's return it,
    // force-showing default columns the user never toggled (absent key);
    // an explicit false (manually hidden) is preserved
    if (!isEmpty(previousSelection)) {
      const merged = { ...previousSelection };
      defaultColumns.forEach((columnName) => {
        if (!(columnName in merged)) {
          merged[columnName] = true;
        }
      });
      return merged;
    }

    // if defaultColumns are set let's return them
    if (!isEmpty(defaultColumns)) {
      return arrayToSelection(defaultColumns);
    }

    // otherwise we fallback on the default behaviour : display all columns
    const retVal = arrayToSelection(getColumnNames());

    return retVal;
  };

  useEffect(() => {
    setSelection(getInitialSelection());
  }, [storageProp]);

  const [selection, setSelection] = useState(getInitialSelection());
  const [modalOpened, setModalOpened] = useState(false);

  const getColumnLabels = () => {
    const retVal = filter(
      children.map(
        (field) =>
          field && {
            source: get(field, ["props", "source"]),
            label: get(field, ["props", "label"]),
          }
      ),
      (item) => item && item.source
    );

    return retVal;
  };

  // updates the storage with the internal state value
  const updateStorage = (newSelection) => {
    storage.set(storageProp, newSelection);
  };

  const toggleColumn = (columnName) => {
    const previousSelection = selection;
    const newSelection = {
      ...previousSelection,
      [columnName]: !previousSelection[columnName],
    };
    setSelection(newSelection);
    updateStorage(newSelection);
  };

  const handleOpen = () => setModalOpened(true);
  const handleClose = () => setModalOpened(false);

  const renderChild = (child) => {
    const source = get(child, ["props", "source"]);

    // Show children without source, or children explicitly visible
    const selectedSource = source && selection[source];
    if (!source || selectedSource) {
      return React.cloneElement(child, {});
    }

    return null;
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginLeft: "1rem",
          marginRight: "1rem",
        }}
      >
        {label && (
          <Typography variant="overline" color="textSecondary">
            {label}
          </Typography>
        )}
        {listSwitch ? listSwitch : <></>}
        {!label && !listSwitch && <>&nbsp;</>}
        {
          <Box m={1}>
            <Button
              variant="outlined"
              mini
              aria-label="add"
              onClick={handleOpen}
            >
              <CustomFieldsIcon />
            </Button>
          </Box>
        }
      </div>
      {modalOpened && (
        <SelectionDialog
          selection={selection}
          columns={getColumnLabels()}
          onColumnClicked={toggleColumn}
          onClose={handleClose}
        />
      )}
      <Datagrid {...props}>
        {children.map((child) => renderChild(child))}
      </Datagrid>
    </div>
  );
};
